import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';

import Actions from 'Actions';

// Utils
import FrameworkUtils from 'utils/framework';
import basketUtils from 'utils/Basket';
import DateUtil from 'utils/Date';
import DeliveryFrequencyUtils from 'utils/DeliveryFrequency';
import StringUtils from 'utils/String';
import skuUtils from 'utils/Sku';

// Selectors
import basketSelector from 'selectors/basket/basketSelector';
import { showEddOnProductPageSelector } from 'viewModel/selectors/testTarget/showEddOnProductPageSelector';
import { shipToHomeSelector } from 'selectors/page/product/fulfillmentOptions/deliveryOptions/shipToHome/shipToHomeSelector';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import { globalModals } from 'utils/globalModals';

// Language Locale
import LanguageLocaleUtils from 'utils/LanguageLocale';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US.js';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA.js';

const { formatSavingAmountString } = DeliveryFrequencyUtils;
const { wrapHOC } = FrameworkUtils;
const { getLocalisedDataFromResource, getTextFromResource, isFRCanada } = LanguageLocaleUtils;
const { preferredZipCodeSelector } = PreferredZipCodeSelector;
const { SHIPPING_AND_HANDLING_INFO, AUTO_REPLENISH_PRODUCT_INFO } = globalModals;

const getText = getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR);

const fields = createSelector(
    basketSelector,
    (_state, ownProps) => ownProps.currentProduct,
    createStructuredSelector({
        autoReplenishTitle: getTextFromResource(getText, 'autoReplenishTitle'),
        deliveryEveryText: getTextFromResource(getText, 'deliveryEvery'),
        mostCommonText: getTextFromResource(getText, 'mostCommon'),
        autoReplenishLegalOptInText: getTextFromResource(getText, 'autoReplenishLegalOptIn'),
        subscriptionNotAvailableText: getTextFromResource(getText, 'subscriptionNotAvailable'),
        autoReplenishFirstTimeLegalShown: getTextFromResource(getText, 'autoReplenishFirstTimeLegalShown', ['{0}', '{1}', '{2}', '{3}']),
        autoReplenishFirstTimeLegalDetails: getTextFromResource(getText, 'autoReplenishFirstTimeLegalDetails'),
        autoReplenishFirstTimeLegalMoreDetails: getTextFromResource(getText, 'autoReplenishFirstTimeLegalMoreDetails', ['{0}', '{1}', '{2}', '{3}']),
        shippingAndReturnsText: getTextFromResource(getText, 'shippingAndReturns'),
        signInText: getTextFromResource(getText, 'signIn'),
        createAccountText: getTextFromResource(getText, 'createAccount'),
        freeStandardShippingText: getTextFromResource(getText, 'freeStandardShipping'),
        toText: getTextFromResource(getText, 'to'),
        getItShippedShipMessage: getTextFromResource(getText, 'getItShippedShipMessage'),
        yourLocationText: getTextFromResource(getText, 'yourLocation')
    }),
    showEddOnProductPageSelector,
    shipToHomeSelector,
    preferredZipCodeSelector,
    globalModalsSelector,
    (basket, product, initialTextResources, showEddOnProductPage, shipToHome, preferredZipCode, globalModalsData) => {
        const { currentSku = {} } = product;
        const { autoReplenishFirstTimeLegalShown, autoReplenishFirstTimeLegalMoreDetails, ...restOfTextResources } = initialTextResources;
        const isReplenishmentEligible = currentSku?.isReplenishmentEligible;
        const listPrice = parseFloat(basketUtils.removeCurrency(currentSku?.listPrice));
        const aboutAutoReplenish = getText('aboutAutoReplen');

        const shouldDisplayEdd = showEddOnProductPage && !currentSku.isOutOfStock;
        const getItShippedDeliveryMessage =
            shouldDisplayEdd && shipToHome?.shipToHomeMessage?.length ? shipToHome?.shipToHomeMessage.split(!isFRCanada() ? ' to' : ' au')[0] : '';
        const locationText = preferredZipCode;
        const isStoreOnly = skuUtils.isStoreOnly(currentSku);

        const { acceleratedPromotion } = currentSku;
        const { childOrderCount, promotionDuration, promotionExpirationDate, baseReplenishmentAdjuster } = acceleratedPromotion || {};

        const autoReplenishPromoDiscountFormatted = formatSavingAmountString(currentSku, null, true);
        const baseAutoReplenishPromoDiscountFormatted = `${Math.ceil(baseReplenishmentAdjuster)}%`;
        const autoReplenishPromoExpirationDateFormatted = DateUtil.getDateInMDYFormat(promotionExpirationDate);

        const autoReplenishPromoLegalCopyShown = StringUtils.format(
            autoReplenishFirstTimeLegalShown,
            autoReplenishPromoDiscountFormatted,
            childOrderCount,
            promotionDuration,
            autoReplenishPromoExpirationDateFormatted
        );

        const autoReplenishLegalMoreDetailsText = StringUtils.format(
            autoReplenishFirstTimeLegalMoreDetails,
            childOrderCount,
            autoReplenishPromoDiscountFormatted,
            promotionDuration,
            baseAutoReplenishPromoDiscountFormatted
        );

        const textResources = {
            ...restOfTextResources,
            aboutAutoReplenish,
            autoReplenishPromoLegalCopyShown,
            autoReplenishLegalMoreDetailsText
        };

        return {
            basket,
            isReplenishmentEligible,
            listPrice,
            textResources,
            shouldDisplayEdd,
            shipToHome,
            getItShippedDeliveryMessage,
            locationText,
            isStoreOnly,
            shippingAndHandlingModal: globalModalsData[SHIPPING_AND_HANDLING_INFO],
            autoReplenishProductInfoModal: globalModalsData[AUTO_REPLENISH_PRODUCT_INFO]
        };
    }
);

const functions = {
    showSignInModal: Actions.showSignInModal,
    showRegisterModal: Actions.showRegisterModal,
    showShippingDeliveryLocationModal: Actions.showShippingDeliveryLocationModal
};

const withGetItShippedWithAutoReplenishmentProps = wrapHOC(connect(fields, functions));

export {
    fields, functions, withGetItShippedWithAutoReplenishmentProps
};
